import React, { useState, useEffect, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form, Badge, Table, Spinner, Dropdown, Modal } from 'react-bootstrap';
import { FaPaperPlane, FaUserFriends, FaFilter, FaEnvelopeOpenText, FaHistory, FaCheckCircle, FaExclamationTriangle, FaPhone, FaWhatsapp, FaTimesCircle, FaTrash, FaTag, FaDownload, FaFileExcel, FaFileCsv } from 'react-icons/fa';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import AdminNavbar from '../../Pages/Navbar/AdminNavbar';
import BaseUrl from '../../ApiServices/BaseUrl';
import {
    CLEANING_MAINTENANCE_POSITIONS,
    CONSTRUCTION_POSITIONS,
    DOMESTIC_HELPER_POSITIONS,
    HEALTHCARE_POSITIONS,
    HOSPITALITY_POSITIONS,
    RETAIL_SALES_POSITIONS,
    SOFTWARE_IT_POSITIONS,
    COMPANY_WORKER_DUAL_CATEGORY_POSITIONS,
    JOB_SEEKER_CATEGORY_OPTIONS,
    HOSPITALITY_CATEGORY,
    SOFTWARE_IT_CATEGORY,
    CONSTRUCTION_CATEGORY,
    RETAIL_SALES_CATEGORY,
    CLEANING_MAINTENANCE_CATEGORY,
    SECURITY_CATEGORY,
    SECURITY_POSITIONS,
    OFFICE_STAFF_CATEGORY,
    OFFICE_STAFF_POSITIONS,
    DRIVER_CATEGORY,
    DRIVER_POSITIONS
} from '../../../Constants/dropdownOptions';
import { COUNTRY_DIAL_CODES } from '../../../Constants/countryDialCodes';
import axios from 'axios';
import Swal from 'sweetalert2';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import './AdminOutreach.css';
import { CAMPAIGN_TEMPLATES } from '../../../Constants/campaignTemplates';

// ── Advanced Download configuration ──
const COMMON_EXPORT_FIELDS = [
    { key: 'name',   label: 'Name' },
    { key: 'email',  label: 'Email' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'plan',   label: 'Subscription Plan' }
];

const AGENCY_EXTRA_FIELDS = [
    { key: 'agency_name',    label: 'Agency Name' },
    { key: 'contact_person', label: 'Contact Person' }
];

const JOB_SEEKER_EXTRA_FIELDS = [
    { key: 'job_position',        label: 'Job Position' },
    { key: 'age',                 label: 'Age' },
    { key: 'religion',            label: 'Religion' },
    { key: 'nationality',         label: 'Nationality' },
    { key: 'verification_status', label: 'Verification Status' }
];

// Maps user type → ordered list of phone sources stored against that user.
// Each entry produces its own export column (no comma-joined values).
const PHONE_SOURCES_BY_USER_TYPE = {
    employer: [
        { key: 'mobile', label: 'Mobile', numberField: 'mobile', codeField: 'mobile_country_code' }
    ],
    agency: [
        { key: 'official_phone',    label: 'Official Phone', numberField: 'official_phone',    codeField: 'official_phone_country_code' },
        { key: 'whatsapp_number_1', label: 'WhatsApp #1',    numberField: 'whatsapp_number_1', codeField: 'whatsapp_number_1_country_code' },
        { key: 'whatsapp_number_2', label: 'WhatsApp #2',    numberField: 'whatsapp_number_2', codeField: 'whatsapp_number_2_country_code' },
        { key: 'whatsapp_number_3', label: 'WhatsApp #3',    numberField: 'whatsapp_number_3', codeField: 'whatsapp_number_3_country_code' },
        { key: 'whatsapp_number_4', label: 'WhatsApp #4',    numberField: 'whatsapp_number_4', codeField: 'whatsapp_number_4_country_code' }
    ],
    job_seeker: [
        { key: 'mobile',          label: 'Mobile',   numberField: 'mobile',          codeField: 'mobile_country_code' },
        { key: 'whatsapp_number', label: 'WhatsApp', numberField: 'whatsapp_number', codeField: 'whatsapp_country_code' }
    ]
};

const PHONE_FORMAT_OPTIONS = [
    { key: 'plus',               label: 'With Country Code (+974XXXX)',  hint: 'No space between country code and number' },
    { key: 'doubleZero',         label: '00 Format (00974XXXX)',          hint: 'Country code prefixed with 00' },
    { key: 'withoutPlus',        label: 'Country Code Only (974XXXX)',    hint: 'Country code without the + symbol' },
    { key: 'startingZero',       label: 'Starting with 0 (0XXXX)',        hint: 'Country code removed, prepended with 0' },
    { key: 'withoutCountryCode', label: 'Number Only (XXXX)',             hint: 'Strips country code and any leading zeros' }
];

const EXPORT_PREFS_STORAGE_KEY = 'gulfworker_instant_export_prefs';

// Pre-computed sorted dial code digits, longest first, for accurate country-code detection
let SORTED_DIAL_CODE_DIGITS = null;
const getSortedDialCodes = () => {
    if (SORTED_DIAL_CODE_DIGITS) return SORTED_DIAL_CODE_DIGITS;
    try {
        const mod = require('../../../Constants/countryDialCodes');
        const list = (mod.COUNTRY_DIAL_CODES || [])
            .map(item => String(item.code || '').replace(/\D/g, ''))
            .filter(Boolean);
        SORTED_DIAL_CODE_DIGITS = [...new Set(list)].sort((a, b) => b.length - a.length);
    } catch (e) {
        SORTED_DIAL_CODE_DIGITS = [];
    }
    return SORTED_DIAL_CODE_DIGITS;
};

const detectCountryAndLocal = (rawMobile = '') => {
    const raw = String(rawMobile || '').trim();
    if (!raw) return { codeDigits: '', localDigits: '' };

    const digits = raw.replace(/\D/g, '');
    if (!digits) return { codeDigits: '', localDigits: '' };

    const hasPlus = raw.startsWith('+');
    const hasDoubleZero = !hasPlus && raw.startsWith('00');

    // Only attempt country-code extraction when the source explicitly carried a prefix.
    // Otherwise a raw local number like "9381850288" would be mis-detected as +93 (Afghanistan).
    if (!hasPlus && !hasDoubleZero) {
        return { codeDigits: '', localDigits: digits };
    }

    const stripped = hasDoubleZero ? digits.replace(/^0+/, '') : digits;

    const dialCodes = getSortedDialCodes();
    for (const dc of dialCodes) {
        if (stripped.startsWith(dc) && stripped.length - dc.length >= 6) {
            return { codeDigits: dc, localDigits: stripped.slice(dc.length) };
        }
    }

    // Fallback: first 1-3 digits as country code
    const codeLen = Math.min(3, Math.max(0, stripped.length - 6));
    return {
        codeDigits: stripped.slice(0, codeLen),
        localDigits: stripped.slice(codeLen)
    };
};

const normalizeDefaultCode = (dialCode = '') =>
    String(dialCode || '').replace(/\D/g, '');

const formatMobileForExport = (rawMobile, format, defaultCountryCode = '', storedCountryCode = '') => {
    const detected = detectCountryAndLocal(rawMobile);
    let { codeDigits, localDigits } = detected;

    if (!codeDigits && !localDigits) return '';

    // Priority: country code embedded in the number > stored country code column > user default
    if (!codeDigits) {
        const stored = normalizeDefaultCode(storedCountryCode);
        if (stored) {
            codeDigits = stored;
        } else if (defaultCountryCode) {
            codeDigits = normalizeDefaultCode(defaultCountryCode);
        }
    }

    switch (format) {
        case 'plus':
            return codeDigits ? `+${codeDigits}${localDigits}` : `+${localDigits}`;
        case 'doubleZero':
            return codeDigits ? `00${codeDigits}${localDigits}` : localDigits;
        case 'withoutPlus':
            return codeDigits ? `${codeDigits}${localDigits}` : localDigits;
        case 'startingZero': {
            const cleanLocal = localDigits.replace(/^0+/, '');
            return cleanLocal ? `0${cleanLocal}` : '';
        }
        case 'withoutCountryCode':
            return localDigits.replace(/^0+/, '');
        default:
            return codeDigits ? `+${codeDigits}${localDigits}` : localDigits;
    }
};

const loadExportPrefs = () => {
    try {
        const raw = localStorage.getItem(EXPORT_PREFS_STORAGE_KEY);
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== 'object') return null;
        return parsed;
    } catch (e) {
        return null;
    }
};

const persistExportPrefs = (fields, formats, fileType, defaultCountryCode, phoneSources, skipEmptyPhones, phoneLayout) => {
    try {
        localStorage.setItem(EXPORT_PREFS_STORAGE_KEY, JSON.stringify({
            fields, formats, fileType, defaultCountryCode, phoneSources, skipEmptyPhones, phoneLayout
        }));
    } catch (e) { /* ignore */ }
};

const parseJobPositionForDisplay = (jobPosition) => {
    if (!jobPosition) return '';
    try {
        const parsed = JSON.parse(jobPosition);
        if (Array.isArray(parsed)) return parsed.join(', ');
    } catch (e) {}
    return String(jobPosition);
};

const AdminInstantMessage = () => {
    const religionOptions = ['Christian', 'Muslim', 'Hindu', 'Buddhism', 'Sikhism', 'Judaism'];
    const ageOptions = Array.from({ length: 63 }, (_, index) => String(index + 18));

    const quillModules = {
        toolbar: [
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'list': 'ordered' }, { 'list': 'bullet' }],
            ['link', 'clean']
        ],
    };

    const [userType, setUserType] = useState('employer');
    const [planFilter, setPlanFilter] = useState('all');
    const [limit, setLimit] = useState(10);
    const [autoSkip, setAutoSkip] = useState(true);
    const [recipientCount, setRecipientCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sending, setSending] = useState(false);
    const [subject, setSubject] = useState('');
    const [message, setMessage] = useState('');
    const [history, setHistory] = useState([]);
    const [recipients, setRecipients] = useState([]);
    const [jobPositionFilter, setJobPositionFilter] = useState('all');
    const [jobCategoryFilter, setJobCategoryFilter] = useState('all');
    const [ageFilter, setAgeFilter] = useState('all');
    const [religionFilter, setReligionFilter] = useState('all');
    const [nationalityFilter, setNationalityFilter] = useState('all');
    const [verificationFilter, setVerificationFilter] = useState('all');
    const [homePageOnly, setHomePageOnly] = useState(false);
    const [jobPositionOptions, setJobPositionOptions] = useState([]);
    const latestRecipientsRequestRef = useRef(0);

    // ── Advanced Download state ──
    const [showAdvancedDownloadModal, setShowAdvancedDownloadModal] = useState(false);
    const [downloadInProgress, setDownloadInProgress] = useState(false);
    const [downloadTotalCount, setDownloadTotalCount] = useState(null);
    const [loadingDownloadCount, setLoadingDownloadCount] = useState(false);
    const [selectedExportFields, setSelectedExportFields] = useState(() => {
        const prefs = loadExportPrefs();
        if (prefs?.fields?.length) return prefs.fields;
        return ['name', 'email', 'mobile'];
    });
    const [selectedPhoneFormats, setSelectedPhoneFormats] = useState(() => {
        const prefs = loadExportPrefs();
        if (prefs?.formats?.length) return prefs.formats;
        return ['plus'];
    });
    const [downloadFileType, setDownloadFileType] = useState(() => {
        const prefs = loadExportPrefs();
        return prefs?.fileType === 'csv' ? 'csv' : 'xlsx';
    });
    const [defaultCountryCode, setDefaultCountryCode] = useState(() => {
        const prefs = loadExportPrefs();
        return prefs?.defaultCountryCode != null ? prefs.defaultCountryCode : '';
    });
    const [selectedPhoneSources, setSelectedPhoneSources] = useState(() => {
        const prefs = loadExportPrefs();
        return prefs?.phoneSources && typeof prefs.phoneSources === 'object' ? prefs.phoneSources : null;
    });
    const [skipEmptyPhones, setSkipEmptyPhones] = useState(() => {
        const prefs = loadExportPrefs();
        return prefs?.skipEmptyPhones !== false;
    });
    const [phoneLayout, setPhoneLayout] = useState(() => {
        const prefs = loadExportPrefs();
        return prefs?.phoneLayout === 'wide' ? 'wide' : 'stacked';
    });

    const nationalityOptions = [...new Set(COUNTRY_DIAL_CODES.map((item) => item.country).filter(Boolean))]
        .sort((a, b) => a.localeCompare(b));

    const filterOptions = (options, searchTerm) => {
        if (!searchTerm) return options;
        return options.filter((option) =>
            String(option).toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const SearchableFilterDropdown = ({
        title,
        field,
        options,
        selectedValue,
        onSelect,
        allLabel,
    }) => {
        const [localSearchTerm, setLocalSearchTerm] = useState('');
        const [isOpen, setIsOpen] = useState(false);

        const filteredOptions = filterOptions(options, localSearchTerm);

        const handleToggle = (nextShow) => {
            setIsOpen(nextShow);
            if (!nextShow) {
                setLocalSearchTerm('');
            }
        };

        const handleSelect = (value) => {
            onSelect(value);
            setLocalSearchTerm('');
            setIsOpen(false);
        };

        return (
            <Dropdown show={isOpen} onToggle={handleToggle} className="w-100">
                <Dropdown.Toggle
                    variant="light"
                    className="custom-select text-start w-100 d-flex justify-content-between align-items-center"
                    id={`dropdown-${field}`}
                >
                    <span className="text-truncate">
                        {selectedValue === 'all' ? allLabel : selectedValue}
                    </span>
                </Dropdown.Toggle>
                <Dropdown.Menu className="w-100" style={{ overflowY: 'hidden' }}>
                    <div className="px-2 py-1">
                        <Form.Control
                            type="text"
                            placeholder={`Search ${title.toLowerCase()}...`}
                            value={localSearchTerm}
                            onChange={(e) => setLocalSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="mb-2"
                            autoFocus
                        />
                    </div>
                    <div style={{ maxHeight: '250px', overflowY: 'auto' }}>
                        <Dropdown.Item
                            active={selectedValue === 'all'}
                            onClick={() => handleSelect('all')}
                        >
                            {allLabel}
                        </Dropdown.Item>
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map((option) => (
                                <Dropdown.Item
                                    key={`${field}-${option}`}
                                    active={selectedValue === option}
                                    onClick={() => handleSelect(option)}
                                >
                                    {option}
                                </Dropdown.Item>
                            ))
                        ) : (
                            <Dropdown.Item disabled>No matches found</Dropdown.Item>
                        )}
                    </div>
                </Dropdown.Menu>
            </Dropdown>
        );
    };

    const userTypeOptions = [
        { label: 'Employer', value: 'employer' },
        { label: 'Agency', value: 'agency' },
        { label: 'Job Seeker', value: 'job_seeker' },
    ];

    const planOptions = {
        employer: [
            { label: 'All Plans', value: 'all' },
            { label: 'Free (No Subscription)', value: 'free' },
            { label: 'Pay Per Use Plan', value: 'pay_per_click' },
            { label: 'Silver Plan', value: 'silver' },
            { label: 'Gold Plan', value: 'gold' },
        ],
        agency: [
            { label: 'All Plans', value: 'all' },
            { label: 'Free (No Subscription)', value: 'free' },
            { label: 'Pay Per Click', value: 'pay_per_click' },
            { label: 'Silver Plan', value: 'silver' },
            { label: 'Gold Plan', value: 'gold' },
        ],
        job_seeker: [
            { label: 'All Plans', value: 'all' },
            { label: 'Free (No Subscription)', value: 'free' },
            { label: 'Pay Per Use Plan', value: 'pay_per_click' },
            { label: 'Silver Plan', value: 'silver' },
            { label: 'Gold Plan', value: 'gold' },
        ],
    };

    const isJobSeeker = userType === 'job_seeker';

    useEffect(() => {
        const fetchJobPositions = async () => {
            try {
                let positions = [];
                if (jobCategoryFilter === 'all') {
                    const res = await axios.get(`${BaseUrl}/api/jobposition`);
                    const apiPositions = Array.isArray(res.data)
                        ? res.data.map((item) => item?.position).filter(Boolean)
                        : [];

                    const fallbackPositions = [
                        ...DOMESTIC_HELPER_POSITIONS,
                        ...HEALTHCARE_POSITIONS,
                        ...HOSPITALITY_POSITIONS,
                        ...SOFTWARE_IT_POSITIONS,
                        ...CONSTRUCTION_POSITIONS,
                        ...RETAIL_SALES_POSITIONS,
                        ...CLEANING_MAINTENANCE_POSITIONS,
                        ...COMPANY_WORKER_DUAL_CATEGORY_POSITIONS,
                    ];
                    positions = [...new Set([...apiPositions, ...fallbackPositions])];
                } else {
                    // Filter based on category
                    const mapping = {
                        'Domestic Helper': DOMESTIC_HELPER_POSITIONS,
                        'Healthcare': HEALTHCARE_POSITIONS,
                        [HOSPITALITY_CATEGORY]: HOSPITALITY_POSITIONS,
                        [SOFTWARE_IT_CATEGORY]: SOFTWARE_IT_POSITIONS,
                        [CONSTRUCTION_CATEGORY]: CONSTRUCTION_POSITIONS,
                        [RETAIL_SALES_CATEGORY]: RETAIL_SALES_POSITIONS,
                        [CLEANING_MAINTENANCE_CATEGORY]: CLEANING_MAINTENANCE_POSITIONS,
                        [SECURITY_CATEGORY]: SECURITY_POSITIONS,
                        [OFFICE_STAFF_CATEGORY]: OFFICE_STAFF_POSITIONS,
                        [DRIVER_CATEGORY]: DRIVER_POSITIONS,
                        'Driver': DRIVER_POSITIONS,
                        'Company Worker': COMPANY_WORKER_DUAL_CATEGORY_POSITIONS
                    };
                    positions = mapping[jobCategoryFilter] || [];
                }

                const uniqueSortedPositions = [...new Set(positions)]
                    .sort((a, b) => a.localeCompare(b));

                setJobPositionOptions(uniqueSortedPositions);
            } catch (err) {
                console.error('Error fetching job positions:', err);
                const uniqueSortedPositions = [...new Set([
                    ...DOMESTIC_HELPER_POSITIONS,
                    ...HEALTHCARE_POSITIONS,
                    ...HOSPITALITY_POSITIONS,
                    ...SOFTWARE_IT_POSITIONS,
                    ...CONSTRUCTION_POSITIONS,
                    ...RETAIL_SALES_POSITIONS,
                    ...CLEANING_MAINTENANCE_POSITIONS,
                    ...COMPANY_WORKER_DUAL_CATEGORY_POSITIONS,
                ])].sort((a, b) => a.localeCompare(b));
                setJobPositionOptions(uniqueSortedPositions);
            }
        };

        fetchJobPositions();
        setJobPositionFilter('all');
    }, [jobCategoryFilter]);

    useEffect(() => {
        if (!isJobSeeker) {
            setJobCategoryFilter('all');
            setJobPositionFilter('all');
            setAgeFilter('all');
            setReligionFilter('all');
            setNationalityFilter('all');
        }
    }, [isJobSeeker]);

    const handleSelectTemplate = (template) => {
        setSubject(template.subject);
        const htmlMsg = template.message
            .split('\n')
            .map(line => line.trim() ? `<p>${line}</p>` : '')
            .join('');
        setMessage(htmlMsg);
    };

    const fetchRecipients = async () => {
        const requestId = ++latestRecipientsRequestRef.current;
        setLoading(true);
        try {
            const res = await axios.get(`${BaseUrl}/api/admin/outreach/recipients`, {
                params: {
                    userType,
                    planFilter,
                    limit: limit === 'all' ? 999999 : limit,
                    offset: 0,
                    excludeSent: autoSkip,
                    ...(isJobSeeker && jobCategoryFilter !== 'all' ? { jobCategory: jobCategoryFilter } : {}),
                    ...(isJobSeeker && jobPositionFilter !== 'all' ? { jobPosition: jobPositionFilter } : {}),
                    ...(isJobSeeker && verificationFilter !== 'all' ? { verificationStatus: verificationFilter } : {}),
                    ...(isJobSeeker && homePageOnly ? { publicOnly: 'true' } : {}),
                    ...(isJobSeeker && ageFilter !== 'all' ? { age: ageFilter } : {}),
                    ...(isJobSeeker && religionFilter !== 'all' ? { religion: religionFilter } : {}),
                    ...(isJobSeeker && nationalityFilter !== 'all' ? { nationality: nationalityFilter } : {}),
                }
            });
            if (requestId !== latestRecipientsRequestRef.current) return;
            setRecipients(res.data);
            setRecipientCount(res.data.length);
        } catch (err) {
            if (requestId !== latestRecipientsRequestRef.current) return;
            console.error('Error fetching recipients:', err);
            setRecipientCount(0);
            setRecipients([]);
        } finally {
            if (requestId === latestRecipientsRequestRef.current) {
                setLoading(false);
            }
        }
    };

    const fetchHistory = async () => {
        try {
            const res = await axios.get(`${BaseUrl}/api/admin/outreach/history`, { params: { limit: 100 } });
            const data = res.data;
            const logs = Array.isArray(data) ? data : (data.logs || []);
            setHistory(logs.filter(h => h.campaign_type === 'instant'));
        } catch (err) {
            console.error('Error fetching history:', err);
        }
    };

    useEffect(() => {
        fetchRecipients();
    }, [userType, planFilter, limit, autoSkip, jobCategoryFilter, jobPositionFilter, ageFilter, religionFilter, nationalityFilter, verificationFilter, homePageOnly]);

    useEffect(() => {
        fetchHistory();
    }, []);

    // ── Advanced Download helpers ──
    const buildFilterParamsForDownload = (overrideLimit) => ({
        userType,
        planFilter,
        limit: overrideLimit,
        offset: 0,
        // intentionally NOT excluding sent — download must include every matching user
        excludeSent: false,
        ...(isJobSeeker && jobCategoryFilter !== 'all' ? { jobCategory: jobCategoryFilter } : {}),
        ...(isJobSeeker && jobPositionFilter !== 'all' ? { jobPosition: jobPositionFilter } : {}),
        ...(isJobSeeker && verificationFilter !== 'all' ? { verificationStatus: verificationFilter } : {}),
        ...(isJobSeeker && homePageOnly ? { publicOnly: 'true' } : {}),
        ...(isJobSeeker && ageFilter !== 'all' ? { age: ageFilter } : {}),
        ...(isJobSeeker && religionFilter !== 'all' ? { religion: religionFilter } : {}),
        ...(isJobSeeker && nationalityFilter !== 'all' ? { nationality: nationalityFilter } : {}),
    });

    const availableExportFields = (() => {
        if (userType === 'agency') return [...AGENCY_EXTRA_FIELDS, ...COMMON_EXPORT_FIELDS];
        if (userType === 'job_seeker') return [...COMMON_EXPORT_FIELDS, ...JOB_SEEKER_EXTRA_FIELDS];
        return COMMON_EXPORT_FIELDS;
    })();

    const availablePhoneSources = PHONE_SOURCES_BY_USER_TYPE[userType] || PHONE_SOURCES_BY_USER_TYPE.employer;

    // selectedPhoneSources is stored as a per-user-type map { employer: ['mobile'], agency: [...] }
    // — that way switching user type doesn't wipe the other type's prefs.
    const activePhoneSourceKeys = (() => {
        const persisted = selectedPhoneSources?.[userType];
        if (Array.isArray(persisted)) {
            const validKeys = new Set(availablePhoneSources.map(s => s.key));
            const filtered = persisted.filter(k => validKeys.has(k));
            return filtered.length ? filtered : availablePhoneSources.map(s => s.key);
        }
        return availablePhoneSources.map(s => s.key); // default: all selected
    })();

    const togglePhoneSource = (key) => {
        setSelectedPhoneSources(prev => {
            const current = prev?.[userType] ?? availablePhoneSources.map(s => s.key);
            const next = current.includes(key) ? current.filter(k => k !== key) : [...current, key];
            return { ...(prev || {}), [userType]: next };
        });
    };

    // Ensure selected fields stay valid when user type changes
    useEffect(() => {
        const validKeys = new Set(availableExportFields.map(f => f.key));
        setSelectedExportFields(prev => {
            const filtered = prev.filter(k => validKeys.has(k));
            return filtered.length ? filtered : ['name', 'email', 'mobile'].filter(k => validKeys.has(k));
        });
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userType]);

    const toggleExportField = (key) => {
        setSelectedExportFields(prev =>
            prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]
        );
    };

    const togglePhoneFormat = (key) => {
        setSelectedPhoneFormats(prev =>
            prev.includes(key) ? prev.filter(item => item !== key) : [...prev, key]
        );
    };

    const openAdvancedDownload = async () => {
        setShowAdvancedDownloadModal(true);
        // Fetch total matching count (using a small limit + a count probe)
        setLoadingDownloadCount(true);
        setDownloadTotalCount(null);
        try {
            const res = await axios.get(`${BaseUrl}/api/admin/outreach/recipients`, {
                params: buildFilterParamsForDownload(999999)
            });
            setDownloadTotalCount(Array.isArray(res.data) ? res.data.length : 0);
        } catch (err) {
            console.error('Failed to fetch download count:', err);
            setDownloadTotalCount(0);
        } finally {
            setLoadingDownloadCount(false);
        }
    };

    const buildExportRows = (allRecipients) => {
        const selectedFields = availableExportFields.filter(f => selectedExportFields.includes(f.key));
        const includeMobile = selectedExportFields.includes('mobile');
        const activeSources = includeMobile
            ? availablePhoneSources.filter(s => activePhoneSourceKeys.includes(s.key))
            : [];
        const phoneColumnKeys = new Set();

        // Helper: build the non-phone (base) part of a row for a recipient
        const buildBaseRow = (r) => {
            const row = {};
            selectedFields.forEach(field => {
                switch (field.key) {
                    case 'name': {
                        if (userType === 'agency') {
                            row[field.label] = r.last_name || r.first_name || '';
                        } else {
                            row[field.label] = `${r.first_name || ''} ${r.last_name || ''}`.trim();
                        }
                        break;
                    }
                    case 'agency_name':
                        row[field.label] = r.first_name || '';
                        break;
                    case 'contact_person':
                        row[field.label] = r.last_name || '';
                        break;
                    case 'email':
                        row[field.label] = r.email || '';
                        break;
                    case 'plan':
                        row[field.label] = r.plan_name || 'Free';
                        break;
                    case 'job_position':
                        row[field.label] = parseJobPositionForDisplay(r.job_position);
                        break;
                    case 'mobile':
                        // handled separately below
                        break;
                    default:
                        row[field.label] = r[field.key] != null ? String(r[field.key]) : '';
                }
            });
            return row;
        };

        const formatLabelFor = (format) =>
            PHONE_FORMAT_OPTIONS.find(f => f.key === format)?.label || 'Number';

        // ── STACKED layout: each phone number gets its own row ──
        if (includeMobile && phoneLayout === 'stacked' && activeSources.length > 0) {
            const stackedRows = [];

            allRecipients.forEach(r => {
                const baseRow = buildBaseRow(r);
                let anyEmittedForUser = false;

                activeSources.forEach(source => {
                    const rawNumber = r[source.numberField];
                    const rawCode   = r[source.codeField];
                    const phoneRow = { ...baseRow };

                    let allFormatsEmpty = true;
                    selectedPhoneFormats.forEach(format => {
                        const colName = `Phone Number (${formatLabelFor(format)})`;
                        phoneColumnKeys.add(colName);
                        const formatted = formatMobileForExport(rawNumber, format, defaultCountryCode, rawCode);
                        phoneRow[colName] = formatted;
                        if (formatted) allFormatsEmpty = false;
                    });

                    if (skipEmptyPhones && allFormatsEmpty) return;
                    stackedRows.push(phoneRow);
                    anyEmittedForUser = true;
                });

                // If the user produced zero phone rows AND the admin chose NOT to skip empties,
                // still emit one empty row so the base fields aren't lost from the file.
                if (!anyEmittedForUser && !skipEmptyPhones) {
                    const placeholder = { ...baseRow };
                    selectedPhoneFormats.forEach(format => {
                        const colName = `Phone Number (${formatLabelFor(format)})`;
                        phoneColumnKeys.add(colName);
                        placeholder[colName] = '';
                    });
                    stackedRows.push(placeholder);
                }
            });

            return { rows: stackedRows, phoneColumnKeys };
        }

        // ── WIDE layout: each (source × format) is its own column ──
        const rows = allRecipients.map(r => {
            const row = buildBaseRow(r);
            activeSources.forEach(source => {
                const rawNumber = r[source.numberField];
                const rawCode   = r[source.codeField];
                selectedPhoneFormats.forEach(format => {
                    const colName = `${source.label} (${formatLabelFor(format)})`;
                    phoneColumnKeys.add(colName);
                    row[colName] = formatMobileForExport(rawNumber, format, defaultCountryCode, rawCode);
                });
            });
            return row;
        });

        const filtered = (includeMobile && skipEmptyPhones && phoneColumnKeys.size > 0)
            ? rows.filter(row => Array.from(phoneColumnKeys).some(col => row[col] && String(row[col]).trim() !== ''))
            : rows;

        return { rows: filtered, phoneColumnKeys };
    };

    const handleAdvancedDownload = async () => {
        if (selectedExportFields.length === 0) {
            return Swal.fire('No Fields', 'Please select at least one field to download.', 'warning');
        }
        if (selectedExportFields.includes('mobile') && selectedPhoneFormats.length === 0) {
            return Swal.fire('No Mobile Format', 'Please select at least one mobile number format.', 'warning');
        }

        try {
            setDownloadInProgress(true);

            const res = await axios.get(`${BaseUrl}/api/admin/outreach/recipients`, {
                params: buildFilterParamsForDownload(999999)
            });
            const all = Array.isArray(res.data) ? res.data : [];

            if (all.length === 0) {
                setDownloadInProgress(false);
                return Swal.fire('No Data', 'No users match the current filters.', 'info');
            }

            const { rows, phoneColumnKeys: mobileColumnKeys } = buildExportRows(all);

            if (rows.length === 0) {
                setDownloadInProgress(false);
                return Swal.fire('No Rows', skipEmptyPhones
                    ? 'All matched users have empty phone numbers for the chosen sources. Turn off "Skip rows where every selected phone is empty" to include them.'
                    : 'No exportable rows were produced.', 'info');
            }

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const headerKeys = Object.keys(rows[0] || {});

            // Force mobile columns to TEXT cell type so Excel doesn't strip the leading "+"
            // and doesn't render long digits in scientific notation.
            if (mobileColumnKeys.size > 0) {
                const range = XLSX.utils.decode_range(worksheet['!ref']);
                for (let C = range.s.c; C <= range.e.c; C += 1) {
                    const headerCell = worksheet[XLSX.utils.encode_cell({ r: 0, c: C })];
                    if (!headerCell || !mobileColumnKeys.has(headerCell.v)) continue;
                    for (let R = 1; R <= range.e.r; R += 1) {
                        const addr = XLSX.utils.encode_cell({ r: R, c: C });
                        const cell = worksheet[addr];
                        if (!cell) continue;
                        cell.t = 's';
                        cell.v = String(cell.v == null ? '' : cell.v);
                        cell.z = '@';
                    }
                }
            }

            worksheet['!cols'] = headerKeys.map(key => ({
                wch: Math.max(key.length, ...rows.map(r => String(r[key] || '').length)) + 2
            }));

            const timestamp = new Date().toISOString().slice(0, 10);
            const userTypeLabel = userTypeOptions.find(o => o.value === userType)?.label?.replace(/\s+/g, '') || userType;
            const filenameBase = `Gulfworker_${userTypeLabel}_${rows.length}_${timestamp}`;

            if (downloadFileType === 'csv') {
                // For CSV: wrap mobile values in ="..." so Excel preserves the leading "+"
                // and treats the cell as text on import. Other tools (Sheets, LibreOffice) still
                // render the value correctly.
                const csvRows = [headerKeys.join(',')];
                rows.forEach(row => {
                    const cells = headerKeys.map(key => {
                        const raw = row[key] == null ? '' : String(row[key]);
                        const escaped = raw.replace(/"/g, '""');
                        if (mobileColumnKeys.has(key) && raw) {
                            return `="${escaped}"`;
                        }
                        return /[",\n]/.test(raw) ? `"${escaped}"` : escaped;
                    });
                    csvRows.push(cells.join(','));
                });
                const csv = '﻿' + csvRows.join('\r\n');
                const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                const url = URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `${filenameBase}.csv`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            } else {
                const workbook = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(workbook, worksheet, 'Recipients');
                XLSX.writeFile(workbook, `${filenameBase}.xlsx`);
            }

            persistExportPrefs(selectedExportFields, selectedPhoneFormats, downloadFileType, defaultCountryCode, selectedPhoneSources, skipEmptyPhones, phoneLayout);
            setShowAdvancedDownloadModal(false);

            Swal.fire({
                icon: 'success',
                title: 'Downloaded!',
                text: `${rows.length} record(s) exported.`,
                timer: 1800,
                showConfirmButton: false
            });
        } catch (err) {
            console.error('Export error:', err);
            Swal.fire('Error', 'Failed to export recipients.', 'error');
        } finally {
            setDownloadInProgress(false);
        }
    };

    const handleSend = async () => {
        if (!subject || !message) {
            return Swal.fire('Wait!', 'Please fill in both subject and message', 'warning');
        }
        if (recipientCount === 0) {
            return Swal.fire('No Recipients', 'No users found with the selected filters', 'warning');
        }

        const isAllMode = limit === 'all';
        const confirm = await Swal.fire({
            title: isAllMode ? 'Send to ALL Matching Users?' : 'Send Instant Message?',
            html: isAllMode
                ? `<div>You are about to email <strong>${recipientCount}</strong> user(s) in a single batch — this is the entire filtered set with no batch cap.</div><div class="mt-2 small text-danger">Make sure this is intentional.</div>`
                : `This will send an email to the matched ${recipientCount} user(s).`,
            icon: isAllMode ? 'warning' : 'question',
            showCancelButton: true,
            confirmButtonText: isAllMode ? `Yes, Send to All ${recipientCount}` : 'Yes, Send Now',
            confirmButtonColor: isAllMode ? '#dc3545' : '#0d6efd'
        });

        if (!confirm.isConfirmed) return;

        setSending(true);
        try {
            const res = await axios.post(`${BaseUrl}/api/admin/outreach/send-instant`, {
                recipients: recipients,
                subject,
                message,
                campaignInfo: { userType, planFilter, limit, offset: 0 }
            });

            if (res.data.success) {
                Swal.fire('Sent!', `${res.data.sent} messages sent successfully. ${res.data.failed} failed.`, 'success');
                setSubject('');
                setMessage('');
                fetchHistory();
                fetchRecipients(); // Refresh list to exclude sent ones
            }
        } catch (err) {
            console.error('Send error:', err);
            Swal.fire('Error', 'Failed to send messages', 'error');
        } finally {
            setSending(false);
        }
    };

    const deleteHistoryLog = async (id) => {
        const confirm = await Swal.fire({
            title: 'Delete Activity Log?',
            text: 'This will permanently remove this campaign record and its history.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, Delete',
            confirmButtonColor: '#dc3545'
        });

        if (!confirm.isConfirmed) return;

        try {
            await axios.delete(`${BaseUrl}/api/admin/outreach/history/${id}`);
            Swal.fire({ icon: 'success', title: 'Deleted!', text: 'Campaign log removed.', timer: 1500, showConfirmButton: false });
            fetchHistory();
        } catch (err) {
            Swal.fire('Error', 'Failed to delete activity log', 'error');
        }
    };

    const clearAllHistory = async () => {
        const confirm = await Swal.fire({
            title: 'Clear ALL Instant History?',
            text: 'This will permanently delete ALL records from recent instant history. This action is irreversible!',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Clear All',
            confirmButtonColor: '#dc3545'
        });

        if (!confirm.isConfirmed) return;

        const { value: password } = await Swal.fire({
            title: 'Password Required',
            text: 'Enter authorization password to clear all history:',
            input: 'password',
            inputPlaceholder: 'Enter password',
            showCancelButton: true,
            confirmButtonText: 'Confirm',
            confirmButtonColor: '#dc3545'
        });

        if (password === '3130') {
            try {
                await axios.delete(`${BaseUrl}/api/admin/outreach/history`, { params: { type: 'instant' } });
                fetchHistory();
                Swal.fire({ icon: 'success', title: 'Cleared!', text: 'All instant history has been deleted.', timer: 1500, showConfirmButton: false });
            } catch (err) {
                Swal.fire('Error', 'Failed to clear history logs', 'error');
            }
        } else if (password !== undefined) {
            Swal.fire('Access Denied', 'Incorrect password.', 'error');
        }
    };

    const [expandedRow, setExpandedRow] = useState(null);
    const [campaignRecipients, setCampaignRecipients] = useState({});
    const [loadingRecipients, setLoadingRecipients] = useState(false);

    // Drag-to-scroll for recipient tables
    const dragScrollRef = useRef(null);
    const isDragging = useRef(false);
    const startX = useRef(0);
    const scrollLeft = useRef(0);

    const handleMouseDown = (e) => {
        const el = dragScrollRef.current;
        if (!el) return;
        isDragging.current = true;
        el.style.cursor = 'grabbing';
        el.style.userSelect = 'none';
        startX.current = e.pageX - el.offsetLeft;
        scrollLeft.current = el.scrollLeft;
    };

    const handleMouseLeave = () => {
        const el = dragScrollRef.current;
        if (!el) return;
        isDragging.current = false;
        el.style.cursor = 'grab';
        el.style.userSelect = '';
    };

    const handleMouseUp = () => {
        const el = dragScrollRef.current;
        if (!el) return;
        isDragging.current = false;
        el.style.cursor = 'grab';
        el.style.userSelect = '';
    };

    const handleMouseMove = (e) => {
        if (!isDragging.current) return;
        e.preventDefault();
        const el = dragScrollRef.current;
        if (!el) return;
        const x = e.pageX - el.offsetLeft;
        const walk = (x - startX.current) * 1.5;
        el.scrollLeft = scrollLeft.current - walk;
    };

    const toggleRow = async (id) => {
        if (expandedRow === id) {
            setExpandedRow(null);
            return;
        }

        setExpandedRow(id);
        if (!campaignRecipients[id]) {
            setLoadingRecipients(true);
            try {
                const res = await axios.get(`${BaseUrl}/api/admin/outreach/history/${id}/recipients`);
                setCampaignRecipients(prev => ({ ...prev, [id]: res.data }));
            } catch (err) {
                console.error('Error fetching campaign recipients:', err);
            } finally {
                setLoadingRecipients(false);
            }
        }
    };

    return (
        <>
            <AdminNavbar />
            <div className="outreach-container" style={{ marginTop: '80px' }}>
                <Container fluid>
                    <Row>
                        <Col lg={12}>
                            <Card className="outreach-card">
                                <div className="outreach-header">
                                    <h4><FaEnvelopeOpenText className="me-2" /> Instant Outreach</h4>
                                    <Badge bg="light" text="primary" className="p-2">
                                        <FaUserFriends className="me-1" /> Targeted Delivery
                                    </Badge>
                                </div>
                                <div className="outreach-body">
                                    {/* 1. Filters */}
                                    <h6 className="mb-3 text-muted"><FaFilter className="me-2" /> Step 1: Filter Recipients</h6>
                                    <div className="filter-section">
                                        <div className="filter-item">
                                            <label>User Category</label>
                                            <select
                                                className="custom-select"
                                                value={userType}
                                                onChange={(e) => {
                                                    setUserType(e.target.value);
                                                    setPlanFilter('all');
                                                }}
                                            >
                                                {userTypeOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="filter-item">
                                            <label>Subscription Plan</label>
                                            <select
                                                className="custom-select"
                                                value={planFilter}
                                                onChange={(e) => setPlanFilter(e.target.value)}
                                            >
                                                {planOptions[userType].map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                            </select>
                                        </div>
                                        <div className="filter-item">
                                            <label>Batch Size (Send Limit)</label>
                                            <select
                                                className="custom-select"
                                                value={limit}
                                                onChange={(e) => setLimit(e.target.value === 'all' ? 'all' : parseInt(e.target.value))}
                                            >
                                                <option value={10}>10 Users</option>
                                                <option value={20}>20 Users</option>
                                                <option value={30}>30 Users</option>
                                                <option value={50}>50 Users</option>
                                                <option value={100}>100 Users</option>
                                                <option value={200}>200 Users</option>
                                                <option value={500}>500 Users</option>
                                                <option value="all">All Matching Users</option>
                                            </select>
                                        </div>
                                        <div className="filter-item">
                                            <label>Automation</label>
                                            <div className="switch-container">
                                                <Form.Check 
                                                    type="switch"
                                                    id="auto-skip-switch"
                                                    label="Auto-skip Contacted"
                                                    checked={autoSkip}
                                                    onChange={(e) => setAutoSkip(e.target.checked)}
                                                    className="fw-bold text-primary"
                                                />
                                            </div>
                                        </div>
                                        {isJobSeeker && (
                                            <>
                                                <div className="filter-item">
                                                    <label>Job Category</label>
                                                    <SearchableFilterDropdown
                                                        title="Job Category"
                                                        field="job_category"
                                                        options={JOB_SEEKER_CATEGORY_OPTIONS}
                                                        selectedValue={jobCategoryFilter}
                                                        onSelect={setJobCategoryFilter}
                                                        allLabel="All Categories"
                                                    />
                                                </div>
                                                <div className="filter-item">
                                                    <label>Job Position</label>
                                                    <SearchableFilterDropdown
                                                        title="Job Position"
                                                        field="job_position"
                                                        options={jobPositionOptions}
                                                        selectedValue={jobPositionFilter}
                                                        onSelect={setJobPositionFilter}
                                                        allLabel="All Positions"
                                                    />
                                                </div>
                                                <div className="filter-item">
                                                    <label>Age</label>
                                                    <SearchableFilterDropdown
                                                        title="Age"
                                                        field="age"
                                                        options={ageOptions}
                                                        selectedValue={ageFilter}
                                                        onSelect={setAgeFilter}
                                                        allLabel="All Ages"
                                                    />
                                                </div>
                                                <div className="filter-item">
                                                    <label>Religion</label>
                                                    <SearchableFilterDropdown
                                                        title="Religion"
                                                        field="religion"
                                                        options={religionOptions}
                                                        selectedValue={religionFilter}
                                                        onSelect={setReligionFilter}
                                                        allLabel="All Religions"
                                                    />
                                                </div>
                                                <div className="filter-item">
                                                    <label>Nationality</label>
                                                    <SearchableFilterDropdown
                                                        title="Nationality"
                                                        field="nationality"
                                                        options={nationalityOptions}
                                                        selectedValue={nationalityFilter}
                                                        onSelect={setNationalityFilter}
                                                        allLabel="All Nationalities"
                                                    />
                                                </div>
                                                <div className="filter-item">
                                                    <label>Verification Status</label>
                                                    <SearchableFilterDropdown
                                                        title="Verification"
                                                        field="verification_status"
                                                        options={['Verified', 'Pending', 'Unpublished', 'Rejected']}
                                                        selectedValue={verificationFilter}
                                                        onSelect={setVerificationFilter}
                                                        allLabel="All Statuses"
                                                    />
                                                </div>
                                                <div className="filter-item d-flex align-items-center mt-4">
                                                    <Form.Check
                                                        type="switch"
                                                        id="home-page-only-switch"
                                                        label="Home Page View Only"
                                                        checked={homePageOnly}
                                                        onChange={(e) => {
                                                            const checked = e.target.checked;
                                                            setHomePageOnly(checked);
                                                            if (checked) {
                                                                setVerificationFilter('Verified');
                                                            }
                                                        }}
                                                    />
                                                    <OverlayTrigger
                                                        trigger={['hover', 'focus']}
                                                        placement="top"
                                                        overlay={
                                                            <Popover id="popover-home-page-only">
                                                                <Popover.Header as="h3">Home Page View Only</Popover.Header>
                                                                <Popover.Body>
                                                                    This matches the home page filters exactly (Only **Verified** users with **Active Paid Plans**).
                                                                </Popover.Body>
                                                            </Popover>
                                                        }
                                                    >
                                                        <span className="ms-2 text-muted cursor-pointer" style={{ fontSize: '0.8rem' }}><FaExclamationTriangle /></span>
                                                    </OverlayTrigger>
                                                </div>
                                            </>
                                        )}
                                    </div>

                                    {/* 2. Recipient Count */}
                                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                                        <h6 className="mb-0 text-muted"><FaUserFriends className="me-2" /> Step 2: Recipients Matched</h6>
                                        <Button
                                            variant="success"
                                            size="sm"
                                            className="fw-bold d-flex align-items-center gap-2"
                                            onClick={openAdvancedDownload}
                                            title="Download all users matching current filters with chosen columns and phone formats"
                                        >
                                            <FaDownload size={12} /> Advanced Download
                                        </Button>
                                    </div>
                                    <div className={`p-4 text-center rounded-3 mb-4 ${recipientCount > 0 ? 'bg-primary text-white' : 'bg-light text-muted'}`}>
                                        {loading ? (
                                            <Spinner animation="border" variant={recipientCount > 0 ? "light" : "primary"} />
                                        ) : (
                                            <>
                                                <div className="small opacity-75">Total Matched Recipients</div>
                                                <div className="h1 mb-0 fw-bold">{recipientCount}</div>
                                                <div className="small">users will receive the email</div>
                                            </>
                                        )}
                                    </div>

                                    {/* 3. Message Composer */}
                                    <h6 className="mb-3 text-muted"><FaEnvelopeOpenText className="me-2" /> Step 3: Compose Message</h6>
                                    <div className="message-composer">
                                        {/* Template Selection */}
                                        <Form.Group className="mb-3">
                                            <div className="d-flex justify-content-between align-items-center mb-1">
                                                <Form.Label className="small fw-bold mb-0">
                                                    <FaTag className="me-1 text-primary" size={11} /> Quick Templates
                                                </Form.Label>
                                                {(subject || message) && (
                                                    <Button 
                                                        variant="link" 
                                                        className="p-0 text-danger text-decoration-none small" 
                                                        style={{ fontSize: '11px' }}
                                                        onClick={() => { setSubject(''); setMessage(''); }}
                                                    >
                                                        Clear Composer
                                                    </Button>
                                                )}
                                            </div>
                                            <Form.Select 
                                                className="custom-input bg-light border-0" 
                                                onChange={(e) => {
                                                    const template = CAMPAIGN_TEMPLATES.find(t => t.id === e.target.value);
                                                    if (template) handleSelectTemplate(template);
                                                }}
                                                value=""
                                                style={{ height: '45px' }}
                                            >
                                                <option value="" disabled>Choose a template...</option>
                                                {CAMPAIGN_TEMPLATES.map(t => (
                                                    <option key={t.id} value={t.id}>{t.name}</option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>

                                        <Form.Group className="mb-3">
                                            <Form.Label>Email Subject</Form.Label>
                                            <Form.Control
                                                type="text"
                                                placeholder="Enter email subject"
                                                className="custom-input"
                                                value={subject}
                                                onChange={(e) => setSubject(e.target.value)}
                                            />
                                        </Form.Group>
                                        <Form.Group className="mb-0">
                                            <Form.Label>Message Body</Form.Label>
                                            <div className="quill-container">
                                                <ReactQuill
                                                    theme="snow"
                                                    value={message}
                                                    onChange={setMessage}
                                                    modules={quillModules}
                                                    placeholder="Write your message here..."
                                                />
                                            </div>
                                        </Form.Group>
                                        <div className="send-btn-container">
                                            <Button
                                                className="premium-btn btn-send"
                                                disabled={sending || recipientCount === 0}
                                                onClick={handleSend}
                                            >
                                                {sending ? <Spinner animation="border" size="sm" /> : <FaPaperPlane />}
                                                {sending ? 'Sending...' : `Send to ${recipientCount} Recipient(s)`}
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            </Card>
                        </Col>
                    </Row>

                    {/* History Table */}
                    <Row className="mt-4">
                        <Col lg={12}>
                            <Card className="outreach-card history-card">
                                <div className="outreach-header bg-dark d-flex justify-content-between align-items-center">
                                    <h4 className="mb-0"><FaHistory className="me-2" /> Recent Instant History</h4>
                                    <Button variant="outline-danger" size="sm" className="fw-bold bg-white text-danger border-white" onClick={clearAllHistory} disabled={history.length === 0}>
                                        <FaTrash className="me-1" size={12} /> Clear All
                                    </Button>
                                </div>
                                <div className="outreach-body">
                                    <Table responsive hover className="premium-table">
                                        <thead>
                                            <tr>
                                                <th>Date</th>
                                                <th>Target</th>
                                                <th>Subject</th>
                                                <th>Stats</th>
                                                <th>Status</th>
                                                <th>Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {history.map(h => (
                                                <React.Fragment key={h.id}>
                                                    <tr>
                                                        <td>{new Date(h.created_at).toLocaleDateString()}</td>
                                                        <td>
                                                            <Badge bg="secondary" className="me-1">{h.user_type}</Badge>
                                                            <Badge bg="light" text="dark">{h.plan_filter}</Badge>
                                                        </td>
                                                        <td>{h.subject}</td>
                                                        <td>
                                                            <div className="stats-container">
                                                                <div className="stat-box">Total: <span className="stat-value">{h.total_recipients}</span></div>
                                                                <div className="stat-box text-success">Sent: <span className="stat-value">{h.sent_count}</span></div>
                                                                <div className="stat-box text-danger">Fail: <span className="stat-value">{h.failed_count}</span></div>
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`status-badge status-${h.status}`}>
                                                                {h.status === 'completed' ? <FaCheckCircle className="me-1" /> : h.status === 'failed' ? <FaExclamationTriangle className="me-1" /> : <Spinner animation="border" size="sm" className="me-1" />}
                                                                {h.status}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            <div className="d-flex gap-2">
                                                                <Button 
                                                                    variant="outline-primary" 
                                                                    size="sm"
                                                                    onClick={() => toggleRow(h.id)}
                                                                    style={{ minWidth: '110px' }}
                                                                >
                                                                    {expandedRow === h.id ? 'Hide' : 'View Recipients'}
                                                                </Button>
                                                                <Button variant="outline-danger" size="sm" onClick={() => deleteHistoryLog(h.id)} title="Delete Log">
                                                                    <FaTrash size={12} />
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                    {expandedRow === h.id && (
                                                        <tr className="expanded-row">
                                                            <td colSpan="6" className="p-0">
                                                                <div className="p-3 bg-light border-bottom">
                                                                    <h6 className="mb-2"><FaUserFriends size={14} className="me-2" /> Campaign Recipients:</h6>
                                                                    {loadingRecipients ? (
                                                                        <div className="text-center py-2"><Spinner animation="border" size="sm" /></div>
                                                                    ) : (
                                                                        <div className="recipient-list">
                                                                            {campaignRecipients[h.id]?.length > 0 ? (
                                                                                <div
                                                                                    className="table-responsive"
                                                                                    ref={dragScrollRef}
                                                                                    onMouseDown={handleMouseDown}
                                                                                    onMouseLeave={handleMouseLeave}
                                                                                    onMouseUp={handleMouseUp}
                                                                                    onMouseMove={handleMouseMove}
                                                                                    style={{ cursor: 'grab' }}
                                                                                >
                                                                                    <Table className="recipient-inner-table table-hover">
                                                                                        <thead>
                                                                                            <tr>
                                                                                                <th>#</th>
                                                                                                <th>Name</th>
                                                                                                <th>Email Address</th>
                                                                                                {h.user_type === 'job_seeker' && (
                                                                                                    <>
                                                                                                        <th>Job Position</th>
                                                                                                        <th>Nationality</th>
                                                                                                        <th>Religion</th>
                                                                                                        <th>Age</th>
                                                                                                    </>
                                                                                                )}
                                                                                                <th>Contact Details</th>
                                                                                                <th>Status</th>
                                                                                                <th>Timestamp</th>
                                                                                            </tr>
                                                                                        </thead>
                                                                                        <tbody>
                                                                                            {campaignRecipients[h.id].map((r, idx) => (
                                                                                                <tr key={idx}>
                                                                                                    <td>{idx + 1}</td>
                                                                                                    <td className="fw-bold">{r.name || 'User'}</td>
                                                                                                    <td>{r.email}</td>
                                                                                                    {h.user_type === 'job_seeker' && (
                                                                                                        <>
                                                                                                            <td>{(() => {
                                                                                                                if (!r.job_position) return <span className="text-muted opacity-50">—</span>;
                                                                                                                try {
                                                                                                                    const parsed = JSON.parse(r.job_position);
                                                                                                                    if (Array.isArray(parsed)) return parsed.join(', ');
                                                                                                                } catch (e) {}
                                                                                                                return r.job_position;
                                                                                                            })()}</td>
                                                                                                            <td>{r.nationality || <span className="text-muted opacity-50">—</span>}</td>
                                                                                                            <td>{r.religion || <span className="text-muted opacity-50">—</span>}</td>
                                                                                                            <td>{r.age || <span className="text-muted opacity-50">—</span>}</td>
                                                                                                        </>
                                                                                                    )}
                                                                                                    <td>
                                                                                                        {r.mobile ? (
                                                                                                            <OverlayTrigger
                                                                                                                trigger="click"
                                                                                                                rootClose
                                                                                                                placement="top"
                                                                                                                overlay={
                                                                                                                    <Popover id={`popover-contact-${h.id}-${idx}`} className="contact-popover">
                                                                                                                        <Popover.Body className="p-1">
                                                                                                                            <div className="d-flex flex-column gap-1">
                                                                                                                                <a 
                                                                                                                                    href={`tel:${r.mobile}`}
                                                                                                                                    className="btn btn-sm btn-light text-primary d-flex align-items-center gap-2 border-0 text-start py-2"
                                                                                                                                >
                                                                                                                                    <div className="bg-primary-subtle p-2 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                                                                                                                                        <FaPhone size={14} className="text-primary" />
                                                                                                                                    </div>
                                                                                                                                    <span className="fw-bold">Direct Call</span>
                                                                                                                                </a>
                                                                                                                                <a 
                                                                                                                                    href={`https://wa.me/${r.mobile.replace(/\D/g, '')}?text=${encodeURIComponent((h.message || '').replace(/<[^>]*>?/gm, ''))}`}
                                                                                                                                    target="_blank"
                                                                                                                                    rel="noopener noreferrer"
                                                                                                                                    className="btn btn-sm btn-light text-success d-flex align-items-center gap-2 border-0 text-start py-2"
                                                                                                                                >
                                                                                                                                    <div className="bg-success-subtle p-2 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '32px', height: '32px' }}>
                                                                                                                                        <FaWhatsapp size={16} className="text-success" />
                                                                                                                                    </div>
                                                                                                                                    <span className="fw-bold">WhatsApp Msg</span>
                                                                                                                                </a>
                                                                                                                            </div>
                                                                                                                        </Popover.Body>
                                                                                                                    </Popover>
                                                                                                                }
                                                                                                            >
                                                                                                                <span className="contact-cell interactive-cell" style={{ cursor: 'pointer' }}>
                                                                                                                    {r.mobile}
                                                                                                                </span>
                                                                                                            </OverlayTrigger>
                                                                                                        ) : (
                                                                                                            <span className="text-muted opacity-50">—</span>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    <td>
                                                                                                        {r.status === 'failed' ? (
                                                                                                            <Badge bg="danger" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                                                                                                                <FaTimesCircle size={10} /> Failed
                                                                                                            </Badge>
                                                                                                        ) : (
                                                                                                            <Badge bg="success" className="d-flex align-items-center gap-1" style={{ width: 'fit-content' }}>
                                                                                                                <FaCheckCircle size={10} /> Sent
                                                                                                            </Badge>
                                                                                                        )}
                                                                                                    </td>
                                                                                                    <td className="text-muted small">{r.sent_at ? new Date(r.sent_at).toLocaleString() : '—'}</td>
                                                                                                </tr>
                                                                                            ))}
                                                                                        </tbody>
                                                                                    </Table>
                                                                                </div>
                                                                            ) : (
                                                                                <div className="text-muted small italic">No detailed recipient logs found for this campaign.</div>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )}
                                                </React.Fragment>
                                            ))}
                                            {history.length === 0 && (
                                                <tr><td colSpan="6" className="text-center py-4">No recent history</td></tr>
                                            )}
                                        </tbody>
                                    </Table>
                                </div>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </div>

            {/* ── Advanced Download Modal ── */}
            <Modal
                show={showAdvancedDownloadModal}
                onHide={() => !downloadInProgress && setShowAdvancedDownloadModal(false)}
                centered
                size="lg"
                backdrop={downloadInProgress ? 'static' : true}
            >
                <Modal.Header closeButton={!downloadInProgress} className="bg-primary text-white border-0">
                    <Modal.Title className="d-flex align-items-center gap-2">
                        <FaDownload size={18} />
                        <div>
                            <div className="fw-bold">Advanced Download</div>
                            <div className="small fw-normal opacity-75" style={{ fontSize: '12px' }}>
                                Choose exact columns and mobile number formats
                            </div>
                        </div>
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="py-4 px-4">
                    <div className="alert alert-info py-2 px-3 mb-3 small d-flex align-items-center gap-2" style={{ fontSize: '13px' }}>
                        <FaFilter size={12} />
                        <span>
                            Exporting all <strong>{userTypeOptions.find(o => o.value === userType)?.label || userType}</strong> users matching the current filters
                            {loadingDownloadCount
                                ? <> · <Spinner animation="border" size="sm" className="ms-1" /></>
                                : downloadTotalCount != null
                                    ? <> · <strong>{downloadTotalCount}</strong> match(es)</>
                                    : null}
                            <span className="text-muted ms-1">(batch size is ignored)</span>
                        </span>
                    </div>

                    <div className="mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                            <Form.Label className="small fw-bold text-uppercase text-muted mb-0" style={{ letterSpacing: '0.5px' }}>
                                Fields to Download
                            </Form.Label>
                            <div className="d-flex gap-2 align-items-center">
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 fw-bold text-primary text-decoration-none"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => setSelectedExportFields(availableExportFields.map(f => f.key))}
                                >
                                    Select All
                                </Button>
                                <span className="text-muted">|</span>
                                <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 fw-bold text-secondary text-decoration-none"
                                    style={{ fontSize: '12px' }}
                                    onClick={() => setSelectedExportFields([])}
                                >
                                    Clear
                                </Button>
                            </div>
                        </div>
                        <Row className="g-2">
                            {availableExportFields.map(field => {
                                const checked = selectedExportFields.includes(field.key);
                                return (
                                    <Col key={field.key} xs={12} sm={6} md={4}>
                                        <label
                                            className="d-flex align-items-center w-100"
                                            style={{
                                                cursor: 'pointer',
                                                fontSize: '13px',
                                                fontWeight: 600,
                                                padding: '8px 12px',
                                                borderRadius: '8px',
                                                border: `1px solid ${checked ? '#0d6efd' : '#dee2e6'}`,
                                                backgroundColor: checked ? 'rgba(13, 110, 253, 0.08)' : '#ffffff',
                                                color: checked ? '#0d6efd' : '#495057',
                                                gap: '10px',
                                                transition: 'all 0.15s ease',
                                                margin: 0
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={checked}
                                                onChange={() => toggleExportField(field.key)}
                                                style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    margin: 0,
                                                    cursor: 'pointer',
                                                    accentColor: '#0d6efd',
                                                    flexShrink: 0
                                                }}
                                            />
                                            <span style={{ lineHeight: '1.2' }}>{field.label}</span>
                                        </label>
                                    </Col>
                                );
                            })}
                        </Row>
                    </div>

                    {selectedExportFields.includes('mobile') && (
                        <div className="mb-4 p-3 rounded border bg-light">
                            <div className="mb-3 pb-3 border-bottom">
                                <Form.Label className="small fw-bold text-uppercase text-muted mb-1 d-block" style={{ letterSpacing: '0.5px' }}>
                                    Phone Layout
                                </Form.Label>
                                <div className="small text-muted mb-2" style={{ fontSize: '12px' }}>
                                    Choose how multiple phone numbers per user are arranged in the file.
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    {[
                                        { key: 'stacked', title: 'All numbers in one column', subtitle: 'Each phone gets its own row; other fields (Name/Email) repeat per row.' },
                                        { key: 'wide',    title: 'Separate column per source', subtitle: 'Mobile, WhatsApp #1, WhatsApp #2 each become their own column.' }
                                    ].map(opt => {
                                        const active = phoneLayout === opt.key;
                                        return (
                                            <button
                                                key={opt.key}
                                                type="button"
                                                onClick={() => setPhoneLayout(opt.key)}
                                                className="flex-fill text-start border-0"
                                                style={{
                                                    minWidth: '220px',
                                                    padding: '10px 12px',
                                                    borderRadius: '8px',
                                                    border: `1px solid ${active ? '#fd7e14' : '#dee2e6'} !important`,
                                                    background: active ? 'rgba(253, 126, 20, 0.08)' : '#ffffff',
                                                    color: active ? '#fd7e14' : '#495057',
                                                    transition: 'all 0.15s ease',
                                                    cursor: 'pointer'
                                                }}
                                            >
                                                <div className="fw-bold" style={{ fontSize: '13px' }}>
                                                    {active ? '● ' : '○ '}{opt.title}
                                                </div>
                                                <div className="small text-muted mt-1" style={{ fontSize: '11px' }}>
                                                    {opt.subtitle}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {availablePhoneSources.length > 1 && (
                                <div className="mb-3 pb-3 border-bottom">
                                    <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                        <Form.Label className="small fw-bold text-uppercase text-muted mb-0" style={{ letterSpacing: '0.5px' }}>
                                            Phone Sources
                                        </Form.Label>
                                        <div className="d-flex gap-2 align-items-center">
                                            <span className="text-muted small" style={{ fontSize: '11px' }}>
                                                Each source becomes its own column.
                                            </span>
                                            <span className="text-muted">|</span>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 fw-bold text-primary text-decoration-none"
                                                style={{ fontSize: '12px' }}
                                                onClick={() => setSelectedPhoneSources(prev => ({ ...(prev || {}), [userType]: availablePhoneSources.map(s => s.key) }))}
                                            >
                                                All
                                            </Button>
                                            <Button
                                                variant="link"
                                                size="sm"
                                                className="p-0 fw-bold text-secondary text-decoration-none"
                                                style={{ fontSize: '12px' }}
                                                onClick={() => setSelectedPhoneSources(prev => ({ ...(prev || {}), [userType]: [] }))}
                                            >
                                                Clear
                                            </Button>
                                        </div>
                                    </div>
                                    <Row className="g-2">
                                        {availablePhoneSources.map(source => {
                                            const checked = activePhoneSourceKeys.includes(source.key);
                                            return (
                                                <Col key={source.key} xs={12} sm={6} md={4}>
                                                    <label
                                                        className="d-flex align-items-center w-100"
                                                        style={{
                                                            cursor: 'pointer',
                                                            fontSize: '13px',
                                                            fontWeight: 600,
                                                            padding: '8px 12px',
                                                            borderRadius: '8px',
                                                            border: `1px solid ${checked ? '#6610f2' : '#dee2e6'}`,
                                                            backgroundColor: checked ? 'rgba(102, 16, 242, 0.08)' : '#ffffff',
                                                            color: checked ? '#6610f2' : '#495057',
                                                            gap: '10px',
                                                            transition: 'all 0.15s ease',
                                                            margin: 0
                                                        }}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => togglePhoneSource(source.key)}
                                                            style={{ width: '16px', height: '16px', margin: 0, cursor: 'pointer', accentColor: '#6610f2', flexShrink: 0 }}
                                                        />
                                                        <span style={{ lineHeight: '1.2' }}>{source.label}</span>
                                                    </label>
                                                </Col>
                                            );
                                        })}
                                    </Row>
                                </div>
                            )}

                            <div className="mb-3 pb-3 border-bottom">
                                <Form.Check
                                    type="switch"
                                    id="skip-empty-phones-switch"
                                    checked={skipEmptyPhones}
                                    onChange={(e) => setSkipEmptyPhones(e.target.checked)}
                                    label={
                                        <span style={{ fontSize: '13px', fontWeight: 600 }}>
                                            Skip rows where every selected phone is empty
                                            <span className="text-muted ms-2" style={{ fontSize: '11px', fontWeight: 400 }}>
                                                (avoids blank rows in the file)
                                            </span>
                                        </span>
                                    }
                                />
                            </div>

                            <div className="mb-3 pb-3 border-bottom">
                                <Form.Label className="small fw-bold text-uppercase text-muted mb-1 d-block" style={{ letterSpacing: '0.5px' }}>
                                    Default Country Code
                                </Form.Label>
                                <div className="small text-muted mb-2" style={{ fontSize: '12px' }}>
                                    Fallback only — the user's actual stored country code (e.g. +93 from the agency / employer profile)
                                    is always used when available. This default is applied only when no code is on file.
                                </div>
                                <Form.Select
                                    size="sm"
                                    value={defaultCountryCode}
                                    onChange={(e) => setDefaultCountryCode(e.target.value)}
                                    style={{ maxWidth: '320px' }}
                                >
                                    <option value="">None — leave numbers as-is</option>
                                    {COUNTRY_DIAL_CODES.map((c) => (
                                        <option key={`${c.country}-${c.code}`} value={c.code}>
                                            {c.code} — {c.country}
                                        </option>
                                    ))}
                                </Form.Select>
                            </div>
                            <div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
                                <Form.Label className="small fw-bold text-uppercase text-muted mb-0" style={{ letterSpacing: '0.5px' }}>
                                    Mobile Formats
                                </Form.Label>
                                <div className="d-flex gap-2 align-items-center">
                                    <span className="text-muted small" style={{ fontSize: '11px' }}>
                                        Each format creates its own column.
                                    </span>
                                    <span className="text-muted">|</span>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 fw-bold text-success text-decoration-none"
                                        style={{ fontSize: '12px' }}
                                        onClick={() => setSelectedPhoneFormats(PHONE_FORMAT_OPTIONS.map(f => f.key))}
                                    >
                                        All
                                    </Button>
                                    <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 fw-bold text-secondary text-decoration-none"
                                        style={{ fontSize: '12px' }}
                                        onClick={() => setSelectedPhoneFormats([])}
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                            <Row className="g-2">
                                {PHONE_FORMAT_OPTIONS.map(format => {
                                    const checked = selectedPhoneFormats.includes(format.key);
                                    return (
                                        <Col key={format.key} xs={12} sm={6}>
                                            <label
                                                className="d-flex flex-column w-100"
                                                style={{
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    padding: '10px 12px',
                                                    borderRadius: '8px',
                                                    border: `1px solid ${checked ? '#198754' : '#dee2e6'}`,
                                                    backgroundColor: checked ? 'rgba(25, 135, 84, 0.08)' : '#ffffff',
                                                    color: checked ? '#198754' : '#495057',
                                                    transition: 'all 0.15s ease',
                                                    margin: 0
                                                }}
                                            >
                                                <div className="d-flex align-items-center gap-2">
                                                    <input
                                                        type="checkbox"
                                                        checked={checked}
                                                        onChange={() => togglePhoneFormat(format.key)}
                                                        style={{
                                                            width: '16px',
                                                            height: '16px',
                                                            margin: 0,
                                                            cursor: 'pointer',
                                                            accentColor: '#198754',
                                                            flexShrink: 0
                                                        }}
                                                    />
                                                    <span style={{ lineHeight: '1.2' }}>{format.label}</span>
                                                </div>
                                                <small className="text-muted mt-1 ms-4" style={{ fontSize: '11px', fontWeight: 400 }}>
                                                    {format.hint}
                                                </small>
                                            </label>
                                        </Col>
                                    );
                                })}
                            </Row>
                        </div>
                    )}

                    <div>
                        <Form.Label className="small fw-bold text-uppercase text-muted mb-2 d-block" style={{ letterSpacing: '0.5px' }}>
                            File Type
                        </Form.Label>
                        <div className="d-inline-flex rounded border bg-light p-1">
                            {['xlsx', 'csv'].map(type => (
                                <Button
                                    key={type}
                                    variant={downloadFileType === type ? 'light' : 'link'}
                                    size="sm"
                                    onClick={() => setDownloadFileType(type)}
                                    className={`fw-bold text-uppercase px-3 ${downloadFileType === type ? 'shadow-sm text-primary' : 'text-secondary text-decoration-none'}`}
                                    style={{ fontSize: '12px' }}
                                >
                                    {type === 'xlsx' ? <FaFileExcel className="me-1" /> : <FaFileCsv className="me-1" />}
                                    {type}
                                </Button>
                            ))}
                        </div>
                    </div>
                </Modal.Body>
                <Modal.Footer className="border-0 px-4 pb-4 d-flex justify-content-between align-items-center flex-wrap gap-2">
                    <div className="small text-muted">
                        {selectedExportFields.length} field(s) selected
                        {selectedExportFields.includes('mobile') && ` · ${selectedPhoneFormats.length} mobile format(s)`}
                    </div>
                    <div className="d-flex gap-2">
                        <Button
                            variant="outline-secondary"
                            onClick={() => setShowAdvancedDownloadModal(false)}
                            disabled={downloadInProgress}
                        >
                            Cancel
                        </Button>
                        <Button
                            variant="primary"
                            className="fw-bold d-flex align-items-center gap-2"
                            onClick={handleAdvancedDownload}
                            disabled={
                                downloadInProgress ||
                                selectedExportFields.length === 0 ||
                                (selectedExportFields.includes('mobile') && selectedPhoneFormats.length === 0)
                            }
                        >
                            {downloadInProgress
                                ? (<><Spinner animation="border" size="sm" /> Preparing...</>)
                                : (<><FaDownload size={13} /> Download</>)
                            }
                        </Button>
                    </div>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default AdminInstantMessage;
